import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Sparkles, Zap, Crown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface QuotaUpsellModalProps {
  open: boolean;
  onClose: () => void;
  quotaData: {
    tier: string;
    limit: number;
    remaining: number;
    resetTime: string;
  } | null;
}

export function QuotaUpsellModal({ open, onClose, quotaData }: QuotaUpsellModalProps) {
  const navigate = useNavigate();

  if (!quotaData) return null;

  const handleUpgrade = () => {
    navigate('/pricing?from=askcircle_quota');
    onClose();
  };

  const resetDate = quotaData.resetTime ? new Date(quotaData.resetTime) : new Date();
  const resetTimeString = resetDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-2xl">Daily Message Limit Reached</DialogTitle>
          <DialogDescription>
            You've used all {quotaData.limit} free messages for today. Your limit resets at {resetTimeString}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {quotaData.tier === 'free' && (
            <>
              <Card className="p-4 border-2 border-primary/20 bg-primary/5">
                <div className="flex items-start gap-3">
                  <Zap className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-base mb-1 flex items-center gap-2">
                      Circle Pro
                      <span className="text-sm font-normal text-muted-foreground">$29/month</span>
                    </h3>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>• 100 messages/day (20x more)</li>
                      <li>• Member-only pricing on tools</li>
                      <li>• AI comparison engine</li>
                      <li>• Priority support</li>
                    </ul>
                  </div>
                </div>
              </Card>

              <Card className="p-4 border-2 border-yellow-500/30 bg-yellow-500/5">
                <div className="flex items-start gap-3">
                  <Crown className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-base mb-1 flex items-center gap-2">
                      Circle Founder
                      <span className="text-sm font-normal text-muted-foreground">One-time</span>
                    </h3>
                    <ul className="space-y-1 text-sm text-muted-foreground">
                      <li>• Unlimited messages forever</li>
                      <li>• Lifetime Pro benefits</li>
                      <li>• Exclusive founder perks</li>
                      <li>• Limited to 500 spots</li>
                    </ul>
                  </div>
                </div>
              </Card>

              <div className="flex gap-3">
                <Button onClick={handleUpgrade} className="flex-1">
                  <Sparkles className="mr-2 h-4 w-4" />
                  Upgrade Now
                </Button>
                <Button onClick={onClose} variant="outline">
                  Maybe Later
                </Button>
              </div>
            </>
          )}

          {quotaData.tier === 'pro' && (
            <>
              <p className="text-sm text-muted-foreground">
                You've reached your daily limit of {quotaData.limit} messages. Your quota will reset at midnight.
              </p>
              <Card className="p-4 border-2 border-yellow-500/30 bg-yellow-500/5">
                <div className="flex items-start gap-3">
                  <Crown className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-semibold text-base mb-1">Upgrade to Founder</h3>
                    <p className="text-sm text-muted-foreground mb-2">
                      Get unlimited messages forever, plus lifetime Pro benefits.
                    </p>
                    <Button onClick={handleUpgrade} size="sm">
                      <Crown className="mr-2 h-4 w-4" />
                      Become a Founder
                    </Button>
                  </div>
                </div>
              </Card>
              <Button onClick={onClose} variant="outline" className="w-full">
                Close
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
